import { create } from "zustand";

export type ChallengeType =
    | "vocabulary_review"
    | "practice_topic"
    | "create_topic"
    | "study_time"
    | "video_shadowing"
    | "video_dictation";

export interface XPGainData {
    xpAmount: number;
    newXp: number;
    newLevel: number;
    levelUp: boolean;
    currentStreak?: number;
    longestStreak?: number;
    streakUpdated?: boolean;
}

export interface StreakData {
    currentStreak: number;
    longestStreak: number;
    isNewRecord: boolean;
}

export interface ChallengeProgressData {
    challengeType: ChallengeType;
    description: string;
    xpReward: number;
}

export interface FreezeUsedData {
    message: string;
    currentStreak: number;
    freezesRemaining: number;
}

export interface MilestoneAchievedData {
    badgeName: string;
    xpBonus: number;
    freezesAwarded: number;
    streakDays: number;
}

export interface GamificationToast {
    id: string;
    type: "xp" | "streak" | "level-up" | "challenge-complete" | "challenge-progress" | "freeze-used" | "milestone-achieved";
    data: XPGainData | StreakData | ChallengeProgressData | FreezeUsedData | MilestoneAchievedData | { message: string };
    timestamp: number;
}

interface GamificationToastState {
    toasts: GamificationToast[];
    showXPGain: (data: XPGainData) => void;
    showStreakUpdate: (data: StreakData) => void;
    showLevelUp: (newLevel: number) => void;
    showChallengeComplete: (xpReward: number) => void;
    showChallengeProgress: (data: ChallengeProgressData) => void;
    showFreezeUsed: (data: FreezeUsedData) => void;
    showMilestoneAchieved: (data: MilestoneAchievedData) => void;
    dismissToast: (id: string) => void;
    clearAllToasts: () => void;
}

const addToast = (toast: Omit<GamificationToast, "id" | "timestamp">) => {
    const newToast: GamificationToast = {
        ...toast,
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        timestamp: Date.now(),
    };

    useGamificationToastStore.setState((state) => ({
        toasts: [...state.toasts, newToast],
    }));

    setTimeout(() => {
        useGamificationToastStore.setState((state) => ({
            toasts: state.toasts.filter((t) => t.id !== newToast.id),
        }));
    }, 4000);
};

export const useGamificationToastStore = create<GamificationToastState>()((set) => ({
    toasts: [],

    showXPGain: (data: XPGainData) => {
        addToast({ type: "xp", data });

        if (data.levelUp) {
            setTimeout(() => {
                addToast({ type: "level-up", data: { message: `Level ${data.newLevel}` } });
            }, 500);
        }

        if (data.streakUpdated && data.currentStreak) {
            setTimeout(() => {
                const longestStreak = data.longestStreak ?? data.currentStreak!;
                const isNewRecord = data.currentStreak! >= longestStreak;
                addToast({
                    type: "streak",
                    data: {
                        currentStreak: data.currentStreak!,
                        longestStreak,
                        isNewRecord,
                    },
                });
            }, 300);
        }
    },

    showStreakUpdate: (data: StreakData) => {
        addToast({ type: "streak", data });
    },

    showLevelUp: (newLevel: number) => {
        addToast({ type: "level-up", data: { message: `Level ${newLevel}` } });
    },

    showChallengeComplete: (xpReward: number) => {
        addToast({ type: "challenge-complete", data: { message: `+${xpReward} XP` } });
    },

    showChallengeProgress: (data: ChallengeProgressData) => {
        addToast({ type: "challenge-progress", data });
    },

    showFreezeUsed: (data: FreezeUsedData) => {
        addToast({ type: "freeze-used", data });
    },

    showMilestoneAchieved: (data: MilestoneAchievedData) => {
        addToast({ type: "milestone-achieved", data });
    },

    dismissToast: (id: string) => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
    },

    clearAllToasts: () => {
        set({ toasts: [] });
    },
}));
